/**
 * 🔴 THE SIDEBAR REPLACES ITSELF (D32). It does not grow a second section.
 *
 * "You should be inside a specific production, and then everything that runs according. So your
 * times, your costs, your payroll is specific to that production. It's not outside of the
 * production. So the production is the project that is being worked on." — the founder.
 *
 * A flat Home · Productions · People · Time · Costs · Payroll list says the opposite: that Time is a
 * PEER of Productions rather than something a production has. So the sidebar has exactly two states,
 * and it is only ever in one of them:
 *
 *   OUTSIDE  the app's own destinations — Productions, Slate, People, Organisations, Admin — in
 *            named groups rather than one flat run of ten rows.
 *   INSIDE   that production's, and nothing else: the way OUT at the top, the production's identity
 *            under it, then Budget · Schedule · People · Time & money · Settings. Every route carries
 *            `?production=<id>`, so a click never loses the context and a copied link opens the same
 *            production for the next person.
 *
 * 🔴 Which entries are which, and what the group headers say, is DATA — the tenant's own tiles
 * (`tenant/types.ts`, `services/tenant`). Nothing in here knows that "costs" is a production's. A
 * shell that hardcoded that lies the day a tenant is configured differently, and the module list is
 * exactly the thing tenants differ on.
 *
 * Pure functions, so the decision table is unit-testable without mounting a component — the same
 * arrangement as `navFilter.ts`, which is the precedent for nav decisions in this shell.
 */
import type { NavBarItem, NavBarSubItem, AppTileScope } from "@/app/types";
import { withProduction, withoutProduction } from "./productionScope";

/** One small-caps header and the rows under it. `label: null` is the ungrouped run above the first header. */
export interface NavGroup {
  label: string | null;
  items: NavBarItem[];
}

/** An entry with nothing said about it is the app's, not a production's. */
export function navScopeOf(item: NavBarItem): AppTileScope {
  return item.scope === "production" ? "production" : "app";
}

/** Entries reachable from anywhere — the sidebar OUTSIDE any production. */
export function appScopedItems(items: NavBarItem[]): NavBarItem[] {
  return items.filter((item) => navScopeOf(item) === "app");
}

/** Entries that belong to one production — the sidebar INSIDE one. */
export function productionScopedItems(items: NavBarItem[]): NavBarItem[] {
  return items.filter((item) => navScopeOf(item) === "production");
}

/**
 * Consecutive entries naming the same section become one group, in the order the tenant wrote them.
 * Consecutive, not sorted: the tenant's order is the reading order, and re-sorting by label would
 * silently rearrange a nav somebody laid out deliberately. Identical to how the sidebar already
 * groups a module's sub-items (`Sidebar.vue:groupSubItems`) — one grouping rule, used twice.
 */
export function groupBySection(items: NavBarItem[]): NavGroup[] {
  const groups: NavGroup[] = [];
  for (const item of items) {
    const label = item.section ?? null;
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.items.push(item);
    else groups.push({ label, items: [item] });
  }
  return groups;
}

/** The same entry, and its drill-down items, carrying the production. */
function routedForProduction(item: NavBarItem, productionId: string): NavBarItem {
  const routed: NavBarItem = { ...item, route: withProduction(item.route, productionId) };
  if (item.items && item.items.length > 0) {
    // A sub-section keeps the context its parent had — `?section=days&production=<id>`, both.
    routed.items = item.items.map(
      (sub): NavBarSubItem => ({ ...sub, route: withProduction(sub.route, productionId) }),
    );
  }
  return routed;
}

/**
 * The sidebar INSIDE a production: that production's entries, grouped, each route carrying the id.
 *
 * Empty when the tenant scopes nothing to a production — and that is what makes the replacement
 * data-driven rather than a shell opinion. `insideProduction` below refuses to swap the sidebar for
 * an empty one, so such a tenant simply never has a second state.
 */
export function navForProduction(items: NavBarItem[], productionId: string): NavGroup[] {
  return groupBySection(productionScopedItems(items).map((item) => routedForProduction(item, productionId)));
}

/** The sidebar OUTSIDE every production: the app's own entries, grouped. */
export function navForApp(items: NavBarItem[]): NavGroup[] {
  return groupBySection(appScopedItems(items));
}

/**
 * 🔴 Which sidebar is drawn. Two facts, both required:
 *
 *   the URL names a production  — the context is a place, not a dropdown's memory, so this is the
 *                                 only thing that can put a person inside one.
 *   the tenant has entries for it — a production sidebar with nothing in it but the way back out is a
 *                                 dead end. A tenant that scopes nothing to a production keeps its one
 *                                 sidebar, and the production simply rides the URL for the modules.
 */
export function insideProduction(items: NavBarItem[], productionId: string | null): boolean {
  return productionId !== null && productionScopedItems(items).length > 0;
}

/**
 * 🔴 **THE MODULE THIS DRAWER HAS BECOME** (D51) — the third sidebar state, beside the app's own and
 * a production's.
 *
 * Devin: *"When we click into admin it should be [like] clicking into a production — all the admin
 * sections [in the sidebar]… with the ALL MODULES button."*
 *
 * A module that names its own sections is a PLACE, and being on one of its routes puts you inside it.
 * The rule is the same one `insideProduction` uses and for the same reason: the URL decides, not a
 * click and not a remembered choice, so a pasted link lands in the context it names.
 *
 * Only app-scoped entries can be drilled. Inside a production the sidebar is already that
 * production's, and a second replacement stacked on the first would leave two ways out and no way to
 * say which one you are leaving.
 */
export function drilledModule(items: NavBarItem[], path: string): NavBarItem | null {
  const here = path.split("?")[0] ?? path;
  let found: NavBarItem | null = null;
  for (const item of appScopedItems(items)) {
    if (!item.items || item.items.length === 0) continue;
    const root = (item.route.split("?")[0] ?? item.route).replace(/\/+$/, "");
    if (root === "" || root === "/") continue;
    if (here !== root && !here.startsWith(`${root}/`)) continue;
    // The LONGEST matching root wins, so `/admin/settings` never resolves to a module at `/admin`
    // that a deeper one is nested under. Same rule as the scope register's literal-beats-parameter.
    if (found === null || root.length > (found.route.split("?")[0] ?? found.route).length) found = item;
  }
  return found;
}

/**
 * Where "← ALL PRODUCTIONS" goes: the way back OUT, with the context taken off.
 *
 * The tenant's `homePath` is its own answer to "where does a person start" — the Desk's is
 * `/productions`, the slate — so it wins whenever it is one of the app-scoped destinations.
 * Otherwise the first app-scoped entry, which is the top of the section a person would look in
 * anyway. Null when the app has no app-scoped entry at all: the caller then draws no control rather
 * than a dead one.
 */
export function wayOutOfProduction(appItems: NavBarItem[], homePath: string | null): string | null {
  const bare = (route: string): string => route.split("?")[0] ?? route;
  const home = homePath !== null && appItems.some((item) => bare(item.route) === bare(homePath)) ? homePath : null;
  const target = home ?? appItems[0]?.route ?? null;
  return target === null ? null : withoutProduction(target);
}
